import React, { useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import DownloaderSection from "@/components/sections/DownloaderSection";
import MainLayout from "@/components/layout/MainLayout";
import FAQSection from "@/components/layout/FAQSection";
import DisclaimerSection from "@/components/layout/DisclaimerSection";
import SupportedPlatforms from "@/components/common/SupportedPlatforms";
import { usePlatformSeo } from "@/lib/hooks/useSeo";
import { platformSlugs } from "@/config/platformSlugs";
import { getPlatformName } from "@/lib/utils/platformUtils";
import Accordion from "@/components/ui/Accordion";

/**
 * DownloaderPage: Component generic cho mọi platform, hỗ trợ đa ngôn ngữ.
 * Sử dụng hệ thống i18n để lấy nội dung từ file JSON, hỗ trợ đa ngôn ngữ.
 * Không hardcode dữ liệu, dễ dàng mở rộng và bảo trì.
 * 
 * =====================================================================
 * NOTE ON SEO IMPLEMENTATION STRATEGY
 * =====================================================================
 * The application uses two different approaches for SEO meta tags:
 * 
 * 1. Direct Head Implementation (used in this file):
 *    - Custom approach for platform-specific pages with complex SEO requirements
 *    - Handles fallbacks across multiple translation namespaces:
 *      a) First tries platform-specific translations (tContent)
 *      b) Falls back to generic SEO templates (tSeo)
 *    - Generates dynamic FAQPage schema.org markup from platform FAQ content
 *    - Uses usePlatformSeo hook specifically for hreflang tags
 * 
 * 2. SEOHead Component (in SEOHead.tsx):
 *    - Used for standard pages (home, about, legal)
 *    - More centralized and consistent approach
 * 
 * This implementation allows for the flexibility needed for platform-specific
 * pages while still maintaining proper multilingual SEO with hreflang tags.
 * When modifying SEO logic, ensure both implementations remain in sync.
 */

interface Props {
  platform: string;
  translations: {
    platform: any;
    commonDownloader: any;
    common: any;
    features: any;
    howTo: any;
    faq: any;
    seo: any;
    platforms: any;
    'error-report': any;
  };
}

// Ánh xạ loại nền tảng với template trong seo-templates.json
const platformTypes: Record<string, string> = {
  dailymotion: "video",
  rumble: "video",
  odnoklassniki: "video",
  vk: "social",
  soundcloud: "music"
  // Có thể thêm nhiều nền tảng khác ở đây
};

const DownloaderPage: React.FC<Props> = ({ platform, translations }) => {
  const router = useRouter();
  const { locale = "en" } = router;

  // Helper to interpolate variables in translation strings
  const replaceVars = (str: string, vars?: Record<string, any>) => {
    if (!vars) return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`));
  };

  // Helper to get translation with fallback and interpolation
  const get = (obj: any, path: string, fallback?: any, vars?: Record<string, any>) => {
    if (!obj) return typeof fallback === "string" ? replaceVars(fallback, vars) : fallback;
    const keys = path.split(".");
    let val = obj;
    for (const k of keys) {
      if (val && typeof val === "object" && k in val) {
        val = val[k];
      } else {
        return typeof fallback === "string" ? replaceVars(fallback, vars) : fallback;
      }
    }
    if (typeof val === "string") {
      return replaceVars(val, vars);
    }
    return typeof fallback === "string" ? replaceVars(fallback, vars) : fallback;
  };

  // Get platform type (video, music, social)
  const platformType = platformTypes[platform] || "video";

  // Sử dụng hook SEO để lấy thông tin hreflang
  const { metaTags } = usePlatformSeo(platform);

  // Get platform name from platforms.json
  const platformName = getPlatformName(platform);

  // Common features for all platforms, retrieved from translation file
  const features = [
    get(translations.features, "noRegistration"),
    get(translations.features, "freeForever"),
    get(translations.features, "highQuality"),
    get(translations.features, "hdSupport"),
    get(translations.features, "fastProcessing"),
  ];

  // H1 title in format "[Platform name] Video Downloader – Quick, Free and Unlimited"
  const title = `${platformName} Video Downloader – Quick, Free and Unlimited`;

  // Short description for the downloader
  const description =
    get(translations.platform, "video.description") ||
    get(translations.seo, `${platformType}.description`, undefined, { platformName });

  // Placeholder text for URL input
  const placeholderText =
    get(translations.platform, "video.placeholder") ||
    get(translations.seo, `${platformType}.placeholder`, undefined, { platformName });

  // Check if platform is not supported
  if (!platformName) return <div>{get(translations.common, "platformNotSupported")}</div>;

  return (
    <MainLayout>
      <Head>
        <title>
          {get(translations.platform, "seo.metaTitle") ||
            get(translations.seo, `${platformType}.metaTitle`, undefined, { platformName })}
        </title>
        <meta
          name="description"
          content={
            get(translations.platform, "seo.metaDescription") ||
            get(translations.seo, `${platformType}.metaDescription`, undefined, { platformName })
          }
        />
        <meta
          name="keywords"
          content={
            get(translations.platform, "seo.keywords") ||
            get(translations.seo, `${platformType}.keywords`, undefined, { platformName: platformName.toLowerCase() })
          }
        />
        <meta
          property="og:title"
          content={
            get(translations.platform, "seo.ogTitle") ||
            get(translations.seo, `${platformType}.ogTitle`, undefined, { platformName })
          }
        />
        <meta
          property="og:description"
          content={
            get(translations.platform, "seo.ogDescription") ||
            get(translations.seo, `${platformType}.ogDescription`, undefined, { platformName })
          }
        />
        <meta property="og:image" content="/downloadmod_logo.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DownloadMod" />
        <meta property="og:url" content={`https://downloadmod.com${locale !== 'en' ? `/${locale}` : ''}/${platformSlugs[platform] || platform}`} />
        <link rel="canonical" href={`https://downloadmod.com${locale !== 'en' ? `/${locale}` : ''}/${platformSlugs[platform] || platform}`} />
        
        {/* Robots meta tags */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            get(translations.platform, "seo.ogTitle") ||
            get(translations.seo, `${platformType}.ogTitle`, undefined, { platformName })
          }
        />
        <meta
          name="twitter:description"
          content={
            get(translations.platform, "seo.ogDescription") ||
            get(translations.seo, `${platformType}.ogDescription`, undefined, { platformName })
          }
        />
        <meta name="twitter:image" content="/downloadmod_logo.png" />
        <meta name="twitter:site" content="@downloadmod" />
        
        {/* Hreflang tags for multilingual SEO */}
        {metaTags.hreflangs && metaTags.hreflangs.map((alt, idx) => (
          <link
            key={alt.hreflang || idx}
            rel="alternate"
            hrefLang={alt.hreflang}
            href={alt.href}
          />
        ))}
        {/* FAQPage Schema.org JSON-LD */}
        {(() => {
          const questionsObj =
            get(translations.platform, "faq.questions") || {};
          const questions = Object.values(questionsObj)
            .filter(
              (q): q is { question: string; answer: string } =>
                !!q && typeof q === "object" && "question" in q && "answer" in q
            )
            .map(q => ({
              question: (q as any).question,
              answer: (q as any).answer,
            }));
          if (questions.length === 0) return null;
          const faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": questions.map(q => ({
              "@type": "Question",
              "name": q.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": q.answer,
              },
            })),
          };
          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          );
        })()}
      </Head>

      {/* Main Downloader Section */}
      <DownloaderSection 
        defaultPlatform={platform as any}
        title={title}
        description={description}
        placeholderText={placeholderText}
        features={features}
      />
      
      {/* Platform-specific content section */}
      <section className="w-full py-16 bg-gray-50 dark:bg-gray-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 gradient-text">
              {get(translations.platform, "howTo.title") ||
                get(translations.commonDownloader, "howTo.title", undefined, { platformName })}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  📋 {get(translations.platform, "howTo.stepsTitle") ||
                    get(translations.commonDownloader, "howTo.stepsTitle")}
                </h3>
                <ol className="list-decimal pl-6 space-y-3 text-gray-600 dark:text-gray-300">
                  {Array.from({ length: 6 }, (_, i) => i + 1).map(step => (
                    <li key={step} className="mb-2">
                      {get(translations.platform, `howTo.steps.${step}`) ||
                        get(translations.commonDownloader, `howTo.steps.${step}`, undefined, { step })}
                    </li>
                  ))}
                </ol>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  ✨ {get(translations.platform, "features.title") ||
                    get(translations.commonDownloader, "features.title")}
                </h3>
                <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map(feature => (
                    <li key={feature} className="mb-2">
                      {get(translations.platform, `features.list.${feature}.title`) ||
                        get(translations.commonDownloader, `features.list.${feature}.title`, undefined, { feature })}:
                      {get(translations.platform, `features.list.${feature}.description`) ||
                        get(translations.commonDownloader, `features.list.${feature}.description`, undefined, { feature })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

              {/* Tips and Tricks */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold mb-4 text-blue-900 dark:text-blue-100">
                  💡 {get(translations.platform, "tips.title") ||
                    get(translations.commonDownloader, "tips.title")}
                </h3>
                {get(translations.platform, "tips.introduction") && (
                  <p className="mb-4 text-blue-800 dark:text-blue-200">
                    {get(translations.platform, "tips.introduction")}
                  </p>
                )}
                <div className="grid md:grid-cols-2 gap-4 text-blue-800 dark:text-blue-200">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map(tip => (
                    <div key={tip} className="p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg">
                      <p className="font-medium">
                        {get(translations.platform, `tips.list.${tip}.title`) ||
                          get(translations.commonDownloader, `tips.list.${tip}.title`, undefined, { tip })}
                      </p>
                      <p className="text-sm mt-1">
                        {get(translations.platform, `tips.list.${tip}.description`) ||
                          get(translations.commonDownloader, `tips.list.${tip}.description`, undefined, { tip })}
                      </p>
                    </div>
                  ))}
                </div>
                {get(translations.platform, "tips.personalExperience") && (
                  <p className="mt-4 italic text-blue-800 dark:text-blue-200">
                    {get(translations.platform, "tips.personalExperience")}
                  </p>
                )}
              </div>
              
              {/* FAQ Section - Accordion */}
              {(get(translations.platform, "faq.title") ||
                get(translations.commonDownloader, "faq.title")) && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-8 shadow-sm">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    ❓ {get(translations.platform, "faq.title") ||
                      get(translations.commonDownloader, "faq.title")}
                  </h3>
                  {get(translations.platform, "faq.introduction") && (
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                      {get(translations.platform, "faq.introduction")}
                    </p>
                  )}
                  {/* Accordion FAQ */}
                  {/* Chuyển đổi từ IIFE sang JSX trực tiếp để tránh lỗi khi prerender */}
                  {(() => {
                    // Lấy danh sách câu hỏi từ file dịch (object -> array)
                    const questionsObj =
                      get(translations.platform, "faq.questions") || {};
                    const questions = Object.values(questionsObj)
                      .filter(
                        (q): q is { question: string; answer: string } =>
                          !!q && typeof q === "object" && "question" in q && "answer" in q
                      )
                      .map(q => ({
                        question: (q as any).question,
                        answer: (q as any).answer,
                      }));
                    
                    // Chỉ render Accordion khi có câu hỏi
                    return questions.length > 0 ? (
                      <Accordion items={questions} />
                    ) : null;
                  })()}
                  {get(translations.platform, "faq.conclusion") && (
                    <p className="mt-6 text-gray-600 dark:text-gray-300">
                      {get(translations.platform, "faq.conclusion")}
                    </p>
                  )}
                </div>
              )}
              
              {/* Additional Content */}
              {(get(translations.platform, "additionalContent.title") ||
                get(translations.commonDownloader, "additionalContent.title")) && (
                <div className="bg-gray-100 dark:bg-gray-800/50 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    🔥 {get(translations.platform, "additionalContent.title") ||
                      get(translations.commonDownloader, "additionalContent.title")}
                  </h3>
                  
                  {get(translations.platform, "additionalContent.introduction") && (
                    <p className="mb-6 text-gray-700 dark:text-gray-300 italic">
                      {get(translations.platform, "additionalContent.introduction")}
                    </p>
                  )}
                  
                  <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 space-y-4">
                    {Array.from({ length: 5 }, (_, i) => i + 1).map(para => (
                      <p key={para}>
                        {get(translations.platform, `additionalContent.paragraphs.${para}`) ||
                          get(translations.commonDownloader, `additionalContent.paragraphs.${para}`)}
                      </p>
                    ))}
                  </div>
                  
                  {get(translations.platform, "additionalContent.conclusion") && (
                    <p className="mt-6 text-gray-700 dark:text-gray-300 font-medium">
                      {get(translations.platform, "additionalContent.conclusion")}
                    </p>
                  )}
                </div>
              )}
              
              {/* Personal Conclusion */}
              {get(translations.platform, "conclusion.title") && (
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    👋 {get(translations.platform, "conclusion.title")}
                  </h3>
                  {get(translations.platform, "conclusion.content") && (
                    <p className="mb-4 text-gray-700 dark:text-gray-300">
                      {get(translations.platform, "conclusion.content")}
                    </p>
                  )}
                  {get(translations.platform, "conclusion.personalNote") && (
                    <p className="mb-4 text-gray-700 dark:text-gray-300">
                      {get(translations.platform, "conclusion.personalNote")}
                    </p>
                  )}
                  {get(translations.platform, "conclusion.signature") && (
                    <p className="italic text-right text-gray-700 dark:text-gray-300">
                      {get(translations.platform, "conclusion.signature")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      
      <SupportedPlatforms />
      <DisclaimerSection />
    </MainLayout>
  );
};



export default DownloaderPage;
